import { AccountBalanceWallet, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, Button, Checkbox, Fade, IconButton, List, ListItem, ListItemButton, ListItemText, Menu, Switch } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { addSelectedPortfolio, removeSelectedPortfolio, toggleCombining } from '@src/app/store/reducers/portfolios';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { ClientPortfolioInfo } from 'clientportalshared';

import { FC, useState } from 'react';

export interface PortfolioSelectionProps {
  allowCombining: boolean;
  disabled?: boolean;
}

const PortfolioSelection: FC<PortfolioSelectionProps> = (props: PortfolioSelectionProps): JSX.Element => {
  const { portfolioInfo, portfoliosIdsSelected, portfoliosCombined } = useAppSelector(selectPortfolios);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();

  const openMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

  const closeMenu = () => setAnchorEl(null);

  const isOpen = Boolean(anchorEl);

  const togglePortfolio = (portfolio: ClientPortfolioInfo) => {
    if (!portfolio.portfolioId) return;

    const enabled = portfoliosIdsSelected.includes(portfolio.portfolioId);
    if (enabled) {
      dispatch(removeSelectedPortfolio(portfolio.portfolioId));
      return;
    }
    dispatch(addSelectedPortfolio(portfolio));
  };

  const isDisabled: boolean =
    !portfolioInfo?.clientPortfolioInfoList || portfolioInfo?.clientPortfolioInfoList?.length === 0 || props.disabled === true;

  return (
    <Box display="flex" flexDirection="column">
      <Button
        disabled={isDisabled}
        endIcon={isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        variant="outlined"
        color="primary"
        sx={{
          width: { xs: 140, md: 220 },
          borderColor: '#D7D7D7',
          color: '#BDBDBD',
          display: { xs: 'none', sm: 'flex' },
        }}
        onClick={openMenu}
      >
        Portfolios
      </Button>
      <IconButton onClick={openMenu} sx={{ display: { xs: 'flex', sm: 'none' }, color: 'black' }} disabled={isDisabled}>
        <AccountBalanceWallet />
      </IconButton>

      {portfolioInfo && portfolioInfo.clientPortfolioInfoList && !isDisabled && (
        <Menu anchorEl={anchorEl} open={isOpen} onClose={closeMenu} TransitionComponent={Fade}>
          <List>
            {portfolioInfo.clientPortfolioInfoList.map((portfolio, index) => (
              <ListItem key={index} disablePadding>
                {portfolio.portfolioId && (
                  <ListItemButton onClick={() => togglePortfolio(portfolio)} dense>
                    <Checkbox edge="start" disableRipple checked={portfoliosIdsSelected.includes(portfolio.portfolioId)} />
                    <ListItemText primary={portfolio.portfolioName} />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
            {portfolioInfo.clientPortfolioInfoList.length > 1 && props.allowCombining && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => dispatch(toggleCombining())} dense>
                  <Switch edge="start" checked={portfoliosCombined} />
                  <ListItemText>Combined</ListItemText>
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Menu>
      )}
    </Box>
  );
};

export default PortfolioSelection;
